import * as React from "react";
import {
    Archive,
    ArrowDown,
    ArrowRightLeft,
    ArrowUp,
    Award,
    Book,
    Boxes,
    Building2,
    Calculator,
    Calendar,
    CalendarDays,
    CalendarRange,
    ChartColumn,
    ChartPie,
    CheckCheck,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Circle,
    CircleAlert,
    CircleCheck,
    CircleDot,
    CircleX,
    ClipboardCheck,
    Code,
    Compass,
    Copy,
    CornerDownRight,
    Download,
    Eye,
    FileCode,
    FilePenLine,
    FilePlus,
    FileText,
    Folder,
    FolderPlus,
    FolderTree,
    GraduationCap,
    House,
    Info,
    Inbox,
    Landmark,
    Layers,
    LoaderCircle,
    List,
    ListChecks,
    ListFilter,
    ListTree,
    Link as LinkIcon,
    Lock,
    LockOpen,
    LogOut,
    Menu,
    MessageSquare,
    Moon,
    Move,
    Network,
    Palette,
    Pause,
    Pen,
    Play,
    Plug,
    Plus,
    Printer,
    RotateCcw,
    RotateCw,
    Save,
    Scale,
    School,
    Search,
    Send,
    SlidersHorizontal,
    Square,
    SquareCheck,
    Sun,
    SquarePen,
    Table,
    TableProperties,
    Tags,
    Trash2,
    TriangleAlert,
    User,
    UserCheck,
    UserPlus,
    UserRoundX,
    Users,
    UsersRound,
    WandSparkles,
    X,
    type LucideIcon,
} from "lucide-react";

/**
 * The single place icons resolve.
 *
 * Call sites are written exactly as before the Bloom Campus migration —
 * `<Icon icon={["fad", "trash"]} />` — so none of them needed to change. The prefix
 * ("fad"/"fas"/"far") is vestigial from the FontAwesome era and is ignored; only the
 * icon name is looked up, against Bloom's real icon vocabulary: inlined Lucide line
 * icons. ICON_MAP below is the FontAwesome-name -> Lucide-component mapping that makes
 * that possible. To add an icon that has no FontAwesome-era name yet, add its Lucide
 * component to the map under whatever name call sites should use — pull it from Lucide,
 * never hand-draw one (Bloom's own rule).
 */

export type IconTuple = readonly [string, string];
export type IconInput = IconTuple | string;

const ICON_MAP: Record<string, LucideIcon> = {
    sliders: SlidersHorizontal,
    "clock-rotate-left": RotateCcw,
    xmark: X,
    "chevron-left": ChevronLeft,
    "chevron-right": ChevronRight,
    "chevron-down": ChevronDown,
    "magnifying-glass": Search,
    swatchbook: Palette,
    "wand-magic-sparkles": WandSparkles,
    plus: Plus,
    "pen-to-square": SquarePen,
    pen: Pen,
    "file-pen": FilePenLine,
    trash: Trash2,
    filter: ListFilter,
    inbox: Inbox,
    "circle-info": Info,
    sitemap: ListTree,
    folder: Folder,
    "folder-plus": FolderPlus,
    "file-circle-plus": FilePlus,
    "arrows-up-down-left-right": Move,
    calculator: Calculator,
    eye: Eye,
    play: Play,
    "triangle-exclamation": TriangleAlert,
    "table-list": TableProperties,
    "floppy-disk": Save,
    "paper-plane": Send,
    "circle-check": CircleCheck,
    "circle-xmark": CircleX,
    "circle-exclamation": CircleAlert,
    lock: Lock,
    "lock-open": LockOpen,
    plug: Plug,
    download: Download,
    building: Building2,
    spinner: LoaderCircle,
    "graduation-cap": GraduationCap,
    house: House,
    award: Award,
    "box-archive": Archive,
    link: LinkIcon,
    rotate: RotateCw,
    pause: Pause,
    compass: Compass,
    "diagram-project": Network,
    "arrow-turn-down-right": CornerDownRight,
    bars: Menu,
    "arrow-right-arrow-left": ArrowRightLeft,
    "right-from-bracket": LogOut,
    copy: Copy,
    "clipboard-check": ClipboardCheck,
    "file-lines": FileText,
    code: Code,
    "file-pdf": FileText,
    "file-code": FileCode,
    "list-check": ListChecks,
    "calendar-days": CalendarDays,
    calendar: Calendar,
    "calendar-week": CalendarRange,
    "rectangle-list": List,
    "layer-group": Layers,
    book: Book,
    cubes: Boxes,
    users: Users,
    "user-plus": UserPlus,
    "user-check": UserCheck,
    "user-graduate": GraduationCap,
    "users-gear": UsersRound,
    "chart-pie": ChartPie,
    "building-columns": Landmark,
    school: School,
    "folder-tree": FolderTree,
    tags: Tags,
    comment: MessageSquare,
    "users-slash": UserRoundX,
    print: Printer,
    sun: Sun,
    moon: Moon,
    // Subject choices and their rules.
    "scale-balanced": Scale,
    "check-double": CheckCheck,
    "table-cells": Table,
    user: User,
    "chart-simple": ChartColumn,
    "arrow-up": ArrowUp,
    "arrow-down": ArrowDown,
    circle: Circle,
    "circle-dot": CircleDot,
    square: Square,
    "square-check": SquareCheck,
};

/** Rendered when a name isn't in ICON_MAP. */
const FALLBACK: LucideIcon = ListFilter; // never hit in practice; keeps the lookup total

function resolve(icon: IconInput): LucideIcon {
    const name = Array.isArray(icon) ? icon[1] : icon;
    return ICON_MAP[name] ?? FALLBACK;
}

export interface IconProps {
    icon: IconInput;
    className?: string;
    spin?: boolean;
    /** No-op: every Lucide icon already renders on a fixed 24x24 box. Kept so old call sites still typecheck. */
    fixedWidth?: boolean;
    title?: string;
    style?: React.CSSProperties;
}

/** Bloom's rule: 1.5px stroke standalone, 2px inside a button label. This app renders most icons
 * inline with text (toolbars, table actions), so 1.75px is used everywhere as a single value. */
const STROKE_WIDTH = 1.75;

export const Icon: React.FC<IconProps> = ({ icon, className, spin, title, style }) => {
    const LucideComponent = resolve(icon);
    return (
        <LucideComponent
            size="1em"
            strokeWidth={STROKE_WIDTH}
            className={[spin ? "animate-spin" : "", className].filter(Boolean).join(" ")}
            style={style}
            aria-label={title}
            role={title ? "img" : undefined}
        />
    );
};

/** The spinner used by every loading state, so they all look the same. */
export const Spinner: React.FC<{ className?: string }> = ({ className = "" }) => (
    <Icon icon={["fad", "spinner"]} spin className={className} />
);

export default Icon;
